import { Invoice, User, UserRole } from '../types';

const CUSTOMER_ROLES = new Set<UserRole>([
  UserRole.CUSTOMER_ADMIN,
  UserRole.CUSTOMER_BRANCH_ADMIN,
  UserRole.CUSTOMER_MANAGER,
  UserRole.CUSTOMER
]);

export const isCustomerRole = (role?: UserRole | null): boolean => Boolean(role && CUSTOMER_ROLES.has(role));

export const isOrganizationSuperAdmin = (user: User | null): boolean =>
  Boolean(user && user.role === UserRole.CUSTOMER_ADMIN && String(user.organizationAdminId || user.id) === String(user.id) && !user.branchId);

export const resolveOrganizationAdminId = (user: User | null): number | string | null => {
  if (!user) return null;
  if (user.role === UserRole.CUSTOMER_ADMIN) return user.id;
  if (user.organizationAdminId) return user.organizationAdminId;
  if (isCustomerRole(user.role)) return user.id;
  return null;
};

export const getUserBranchScopeId = (user: User | null): number | null => {
  if (!user || isOrganizationSuperAdmin(user)) return null;
  return user.branchId ?? null;
};

export const canAccessBranchRecord = (branchId: number | null | undefined, user: User | null): boolean => {
  const scopeBranchId = getUserBranchScopeId(user);
  return scopeBranchId === null || String(branchId || '') === String(scopeBranchId);
};

export const getOrderOwningCustomerId = (user: User | null): number | string | null => {
  if (!user) return null;

  if (
    (user.role === UserRole.CUSTOMER || user.role === UserRole.CUSTOMER_MANAGER || user.role === UserRole.CUSTOMER_BRANCH_ADMIN) &&
    user.organizationAdminId
  ) {
    return user.organizationAdminId;
  }

  return user.id;
};

export const canUserAccessOrderBranch = (order: { branchId?: number | null; customerAdminId?: number | string | null; customerId?: number | string | null }, user: User | null): boolean => {
  if (!user) return false;
  const orgAdminId = resolveOrganizationAdminId(user);
  if (
    String(order.customerAdminId || '') !== String(orgAdminId || '') &&
    String(order.customerId || '') !== String(orgAdminId || '')
  ) {
    return false;
  }
  return canAccessBranchRecord(order.branchId, user);
};

export const canUserAccessInvoice = (invoice: Invoice, user: User | null): boolean => {
  if (!user) return false;

  const scopedIds = new Set<string>([String(user.id)]);
  const owningCustomerId = getOrderOwningCustomerId(user);

  if (owningCustomerId !== null) {
    scopedIds.add(String(owningCustomerId));
  }

  if (user.organizationAdminId) {
    scopedIds.add(String(user.organizationAdminId));
  }

  return scopedIds.has(String(invoice.customerId));
};
